import React from 'react';
import { FileText, Settings, Users, Award, Database, AlertCircle, CreditCard } from 'lucide-react';

/**
 * Terms Step - Contract terms and conditions
 * 
 * ⭐ E9: Start Date is DATE field type
 * ⭐ E10: Shows Platform deployment and syncs to Terms
 * ⭐ E15: Shows End Date field when Custom term selected
 * ⭐ M16: Payment Terms is editable and reflects on PDF
 * ⭐ M17: Updated hosting labels for clarity
 */
const TermsStep = ({ quoteData, setQuoteData }) => {
  const updateTerm = (field, value) => {
    setQuoteData({
      ...quoteData,
      terms: { ...quoteData.terms, [field]: value }
    });
  };

  // ⭐ E10: Get deployment from Platform step
  const platformDeployment = quoteData.deployment;
  const termsDeployment = quoteData.terms?.deploymentType;
  
  // ⭐ E10: Check if there's a mismatch
  const deploymentMismatch = platformDeployment && termsDeployment && 
    platformDeployment !== termsDeployment;

  // ⭐ E10: Sync deployment from Platform to Terms on mount
  React.useEffect(() => {
    if (platformDeployment && !termsDeployment) {
      updateTerm('deploymentType', platformDeployment);
    }
  }, [platformDeployment]);

  // ⭐ M17: Deployment options with clear labels
  const deploymentOptions = [
    { 
      value: 'On-Premise', 
      label: 'Customer-Managed (On-Premise)',
      desc: 'Installed in your data center or private cloud (AWS, Azure, etc.)'
    },
    { 
      value: 'Private Hosting', 
      label: 'SplashBI-Hosted Private Cloud',
      desc: 'Dedicated environment managed by SplashBI for your organization'
    },
    { 
      value: 'Public Cloud', 
      label: 'Fully Managed SaaS',
      desc: 'Multi-tenant cloud - no infrastructure to manage'
    }
  ];

  // ⭐ M16: Payment terms options
  const paymentTermsOptions = [
    { value: 'Net 30', label: 'Net 30 days' },
    { value: 'Net 45', label: 'Net 45 days' },
    { value: 'Net 60', label: 'Net 60 days' },
    { value: 'Net 90', label: 'Net 90 days' },
    { value: 'Due on Receipt', label: 'Due on Receipt' },
    { value: '50% Upfront, 50% Net 30', label: '50% Upfront, 50% Net 30' },
    { value: 'Annual Prepay', label: 'Annual Prepay (5% discount)' },
    { value: 'Quarterly', label: 'Quarterly Payments' },
    { value: 'Custom', label: 'Custom Terms' }
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">Terms & Conditions</h2>
      <p className="text-slate-600 mb-8">Define contract terms and service details</p>
      
      <div className="space-y-8">
        {/* Contract Terms */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <FileText size={18} className="text-blue-600" />
            Contract Terms
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Contract Term</label>
              <select
                value={quoteData.terms?.contractTerm || '1 Year'}
                onChange={(e) => updateTerm('contractTerm', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="1 Year">1 Year</option>
                <option value="2 Years">2 Years</option>
                <option value="3 Years">3 Years</option>
                <option value="5 Years">5 Years</option>
                <option value="Custom">Custom</option>
              </select>
            </div>
            
            {/* ⭐ E9: Start Date - DATE field type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={quoteData.terms?.startDate || ''}
                onChange={(e) => updateTerm('startDate', e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  !quoteData.terms?.startDate ? 'border-amber-300 bg-amber-50' : 'border-slate-300'
                }`}
              />
              {!quoteData.terms?.startDate && (
                <p className="text-xs text-amber-600 mt-1">Required for quote generation</p>
              )}
            </div>
            
            {/* ⭐ E15: End Date - shown only for Custom term */}
            {quoteData.terms?.contractTerm === 'Custom' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={quoteData.terms?.endDate || ''}
                  onChange={(e) => updateTerm('endDate', e.target.value)}
                  min={quoteData.terms?.startDate || ''}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {!quoteData.terms?.endDate && (
                  <p className="text-xs text-red-500 mt-1">End date required for custom terms</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ⭐ M16: Payment Terms - Enhanced */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <CreditCard size={18} className="text-green-600" />
            Payment Terms
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Payment Terms</label>
              <select
                value={quoteData.terms?.paymentTerms || 'Net 30'}
                onChange={(e) => updateTerm('paymentTerms', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {paymentTermsOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            {/* Custom payment terms field */}
            {quoteData.terms?.paymentTerms === 'Custom' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Custom Payment Terms <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={quoteData.terms?.customPaymentTerms || ''}
                  onChange={(e) => updateTerm('customPaymentTerms', e.target.value)}
                  placeholder="Describe custom payment schedule..."
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Payment Method</label>
              <select
                value={quoteData.terms?.paymentMethod || 'Bank Wire or Check'}
                onChange={(e) => updateTerm('paymentMethod', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Bank Wire or Check">Bank Wire or Check</option>
                <option value="ACH Transfer">ACH Transfer</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Purchase Order">Purchase Order</option>
              </select>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Any applicable taxes will be paid by the Customer. 
              Payment terms will be reflected on the final contract and customer quote.
            </p>
          </div>
        </div>

        {/* ⭐ M17: Deployment & Infrastructure - Updated Labels */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Settings size={18} className="text-blue-600" />
            Deployment & Infrastructure
          </h3>
          
          {/* ⭐ E10: Show Platform deployment selection */}
          {platformDeployment && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Platform Selection:</strong> {
                  deploymentOptions.find(d => d.value === platformDeployment)?.label || platformDeployment
                }
              </p>
            </div>
          )}
          
          {/* ⭐ E10: Warning if mismatch */}
          {deploymentMismatch && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                <strong>Mismatch:</strong> Platform shows different deployment than Terms. 
                Consider updating to match.
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Deployment Type
                {platformDeployment && (
                  <button
                    type="button"
                    onClick={() => updateTerm('deploymentType', platformDeployment)}
                    className="ml-2 text-xs text-blue-600 hover:underline"
                  >
                    (Sync from Platform)
                  </button>
                )}
              </label>
              <select
                value={quoteData.terms?.deploymentType || ''}
                onChange={(e) => updateTerm('deploymentType', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Deployment...</option>
                {deploymentOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {quoteData.terms?.deploymentType && (
                <p className="text-xs text-slate-500 mt-1">
                  {deploymentOptions.find(d => d.value === quoteData.terms.deploymentType)?.desc}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Instances</label>
              <input
                type="text"
                value={quoteData.terms?.instances || '1 Production, 1 Non-Production'}
                onChange={(e) => updateTerm('instances', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="col-span-1 md:col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={quoteData.terms?.vpnRequired ?? true}
                  onChange={(e) => updateTerm('vpnRequired', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">VPN Required (Customer-provided)</span>
              </label>
            </div>
          </div>
        </div>

        {/* Professional Services */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Users size={18} className="text-blue-600" />
            Professional Services
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Implementation</label>
              <input
                type="text"
                value={quoteData.terms?.implementation || 'Standard functionality, Onshore/Offshore'}
                onChange={(e) => updateTerm('implementation', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Training</label>
              <select
                value={quoteData.terms?.training || 'Offsite/Remote'}
                onChange={(e) => updateTerm('training', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Offsite/Remote">Offsite/Remote</option>
                <option value="Onsite">Onsite</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Onshore Rates</label>
              <input
                type="text"
                value={quoteData.terms?.ratesOnshore || '$180-225/hr'}
                onChange={(e) => updateTerm('ratesOnshore', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Offshore Rates</label>
              <input
                type="text"
                value={quoteData.terms?.ratesOffshore || '$55-65/hr'}
                onChange={(e) => updateTerm('ratesOffshore', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="col-span-1 md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Travel Expenses</label>
              <input
                type="text"
                value={quoteData.terms?.travelExpenses || 'Billed separately at actual cost'}
                onChange={(e) => updateTerm('travelExpenses', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Support */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Award size={18} className="text-blue-600" />
            Support
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Authorized Users</label>
              <input
                type="text"
                value={quoteData.terms?.supportUsers || '5'}
                onChange={(e) => updateTerm('supportUsers', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Support Timing</label>
              <input
                type="text"
                value={quoteData.terms?.supportTiming || 'Standard (per Support Policy)'}
                onChange={(e) => updateTerm('supportTiming', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Version Coverage</label>
              <input
                type="text"
                value={quoteData.terms?.supportVersions || 'Current + 1 prior version'}
                onChange={(e) => updateTerm('supportVersions', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Managed Services */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Database size={18} className="text-blue-600" />
            Managed Services
          </h3>
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={quoteData.terms?.managedServicesIncluded ?? false}
                onChange={(e) => updateTerm('managedServicesIncluded', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-slate-700">Include Managed Services</span>
            </label>
            
            {quoteData.terms?.managedServicesIncluded && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Managed Services Details</label>
                <textarea
                  value={quoteData.terms?.managedServicesDetails || ''}
                  onChange={(e) => updateTerm('managedServicesDetails', e.target.value)}
                  rows={3}
                  placeholder="Infrastructure monitoring, upgrades, quarterly OS updates"
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Custom Report Maintenance</label>
              <input
                type="text"
                value={quoteData.terms?.customReportMaintenance || 'Not included, requires SOW'}
                onChange={(e) => updateTerm('customReportMaintenance', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* General Terms */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4">General Terms</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Hyper Care / Adoption Services</label>
              <input
                type="text"
                value={quoteData.terms?.hyperCare || 'Time & Material basis'}
                onChange={(e) => updateTerm('hyperCare', e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={quoteData.terms?.sowProvided ?? true}
                onChange={(e) => updateTerm('sowProvided', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-slate-700">Detailed SOW will be provided at contract signing</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsStep;
